import { addDays, startOfDay } from "../../../utils/dateBasics";
import React, { createContext, useContext, useMemo, useState } from "react";
import type { Tag } from "../../../graphql/operations";
import { useCachedGql } from "../../../hooks/useCachedGql";

export const allTimeRanges = ["morning", "afternoon", "evening"] as const;
export type TimeRange = (typeof allTimeRanges)[number];

const useSearchProviderValues = (skip?: boolean) => {
  const [locationId, setLocationId] = useState<undefined | string>(undefined);
  const [countryId, setCountryId] = useState<undefined | string>(undefined);
  const [from, setFrom] = useState<Date>(startOfDay(new Date()));
  const [until, setUntil] = useState<Date>(startOfDay(addDays(new Date(), 3)));
  const [timeRanges, setTimeRanges] = useState([...allTimeRanges]);
  const [numGuests, setNumGuests] = useState<number>(2);
  const [tags, setTags] = useState<Set<Tag["id"]>>(new Set());
  const tagArray = useMemo(() => Array.from(tags), [tags]);

  const { data: tagOptionData } = useCachedGql("getTagOptions", {
    skip,
    mode: "server",
  });

  const tagOptions = useMemo(
    () =>
      new Map(
        (tagOptionData?.tags.records ?? []).map(({ id, name }) => [id, name]),
      ),
    [tagOptionData],
  );

  return {
    countryId,
    from,
    locationId,
    numGuests,
    setCountryId,
    setFrom,
    setLocationId,
    setNumGuests,
    setTags,
    setTimeRanges,
    setUntil,
    tagArray,
    tagOptions,
    tags,
    timeRanges,
    until,
  };
};

const SearchContext = createContext<ReturnType<
  typeof useSearchProviderValues
> | null>(null);

const SearchProvider = ({
  children,
  disabled,
}: React.PropsWithChildren & { disabled?: boolean }) => {
  return (
    <SearchContext.Provider value={useSearchProviderValues(disabled)}>
      {children}
    </SearchContext.Provider>
  );
};

const useSearchContext = () => {
  const context = useContext(SearchContext);
  if (!context)
    throw new Error("useSearchContext must be used within a SearchProvider");
  return context;
};

export { SearchProvider, useSearchContext };
